import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Coins, Loader2, Plus, RotateCcw } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents, parseEurosToCents } from '../../lib/money'
import { useIssuers } from '../accounting/common'
import {
  FundsProvisionStatusLabel,
  PaymentMethodLabel,
  type FundsProvisionDto,
  type FundsProvisionListResponse,
  type PaymentMethodT,
} from '@toga/shared'

interface Props {
  clientId: string
  caseId?: string
  /** Si true, oculta el botón crear (solo lectura). */
  readOnly?: boolean
}

export default function FundsProvisionPanel({ clientId, caseId, readOnly }: Props) {
  const [items, setItems] = useState<FundsProvisionDto[]>([])
  const [availableTotal, setAvailableTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ clientId })
      if (caseId) params.set('caseId', caseId)
      const resp = await api.get<FundsProvisionListResponse>(
        `/funds-provisions?${params.toString()}`
      )
      setItems(resp.items)
      setAvailableTotal(resp.availableTotalCents)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [clientId, caseId])

  useEffect(() => {
    void reload()
  }, [reload])

  const onRefund = async (p: FundsProvisionDto) => {
    const txt = prompt(
      `Importe a devolver (saldo disponible ${formatCents(p.availableCents)}):`,
      (p.availableCents / 100).toFixed(2).replace('.', ',')
    )
    if (!txt) return
    const cents = parseEurosToCents(txt)
    if (cents <= 0 || cents > p.availableCents) {
      alert('Importe inválido')
      return
    }
    try {
      await api.post(`/funds-provisions/${p.id}/refund`, { amountCents: cents })
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      alert(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Coins size={14} className="text-toga-300" />
          Provisiones de fondos
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">({items.length})</span>
          )}
        </h3>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <Plus size={12} /> Recibir
          </button>
        )}
      </div>

      <div className="text-xs t-muted mb-3">
        Saldo disponible:{' '}
        <span className="font-mono t-primary font-semibold text-base">
          {formatCents(availableTotal)}
        </span>
      </div>

      {error && (
        <p className="text-xs text-rose-300 mb-3">{error}</p>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5 py-2">
          <Loader2 size={11} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint py-3 text-center">Sin provisiones registradas.</p>
      ) : (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((p) => (
            <li key={p.id} className="py-2.5">
              <div className="flex items-baseline gap-2">
                <span className="t-secondary text-xs">
                  {new Date(p.receivedAt).toLocaleDateString('es-ES')}
                </span>
                <span className="ml-auto font-mono t-primary font-semibold">
                  {formatCents(p.amountCents)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] t-faint mt-0.5">
                <span>{PaymentMethodLabel[p.method]}</span>
                {p.issuerName && <span>· {p.issuerName}</span>}
                {p.reference && <span className="font-mono">· {p.reference}</span>}
                <span className="ml-auto">
                  Disponible:{' '}
                  <span className="t-primary font-mono">{formatCents(p.availableCents)}</span>
                </span>
              </div>
              {p.description && (
                <p className="text-xs t-secondary mt-1">{p.description}</p>
              )}
              <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                <StatusBadge status={p.status} />
                {p.ledgerBooked && (
                  <span
                    className="inline-flex px-1.5 py-0.5 rounded text-[10px] border border-toga-500/30 text-toga-300"
                    title="El cobro de esta provisión está en el diario del emisor (anticipo de cliente)"
                  >
                    En contabilidad
                  </span>
                )}
                {p.appliedCents > 0 && (
                  <span className="text-emerald-300">
                    Aplicado: {formatCents(p.appliedCents)}
                  </span>
                )}
                {p.refundedCents > 0 && (
                  <span className="text-amber-300">
                    Devuelto: {formatCents(p.refundedCents)}
                  </span>
                )}
                {!readOnly && p.availableCents > 0 && (
                  <button
                    type="button"
                    onClick={() => void onRefund(p)}
                    className="ml-auto t-secondary hover:text-amber-300 inline-flex items-center gap-1"
                    title="Devolver saldo"
                  >
                    <RotateCcw size={10} /> Devolver
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showNew && (
        <NewProvisionModal
          clientId={clientId}
          caseId={caseId}
          onClose={() => setShowNew(false)}
          onSaved={async () => {
            setShowNew(false)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: FundsProvisionDto['status'] }) {
  const STYLES = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    APPLIED: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    REFUNDED: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  } as const
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {FundsProvisionStatusLabel[status]}
    </span>
  )
}

function NewProvisionModal({
  clientId,
  caseId,
  onClose,
  onSaved,
}: {
  clientId: string
  caseId?: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethodT>('TRANSFERENCIA')
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [issuerId, setIssuerId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const issuers = useIssuers().filter((i) => i.active)
  const chosen = issuers.find((i) => i.id === issuerId)
  const showIssuer = issuers.length > 1 || issuers.some((i) => i.accountingRegime === 'PGC')

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const cents = parseEurosToCents(amount)
    if (cents <= 0) {
      setError('Importe inválido')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post('/funds-provisions', {
        clientId,
        caseId: caseId ?? null,
        receivedAt: new Date(receivedAt).toISOString(),
        amountCents: cents,
        method,
        description: description || null,
        reference: reference || null,
        issuerId: issuerId || null,
      })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  // En un portal a `body`: el panel tiene `animate-slideUp` (transform) y un `fixed` dentro se
  // quedaría confinado a él.
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">Recibir provisión</h3>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Fecha</span>
            <input
              type="date"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Importe</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
              required
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">Método</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethodT)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          >
            {Object.entries(PaymentMethodLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        {showIssuer && (
          <label className="block">
            <span className="text-xs t-muted">Emisor que recibe el dinero</span>
            <select
              value={issuerId}
              onChange={(e) => setIssuerId(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            >
              <option value="">Automático (el del expediente o el cliente)</option>
              {issuers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {i.accountingRegime === 'PGC' ? ' · contabilidad PGC' : ''}
                </option>
              ))}
            </select>
            {chosen?.accountingRegime === 'PGC' && (
              <span className="text-[11px] t-faint">
                El cobro entra en su diario como anticipo de cliente (438).
              </span>
            )}
          </label>
        )}

        <label className="block">
          <span className="text-xs t-muted">Concepto / descripción</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Anticipo procedimiento monitorio..."
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        <label className="block">
          <span className="text-xs t-muted">Referencia</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="N.º operación o recibo"
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Recibir
          </button>
        </div>
      </form>
    </div>,
    document.body
  )
}
